// Lightweight localStorage wrapper. Phase 2 will add Supabase sync atop these.

import type { DrawMode, GameState, ScoringSystem } from "../game/types.ts";
import { DEFAULT_SCORING_SYSTEM } from "../game/play-score.ts";

const PREFIX = "just-solitaire:v1:";
const K_STATS = `${PREFIX}stats`;
const K_PROGRESS = `${PREFIX}progress`;
const K_SETTINGS = `${PREFIX}settings`;

export interface Stats {
  cumulativeXp: number;
  gamesPlayed: number;
  gamesWon: number;
  bestTimeSecOne: number | null;
  bestTimeSecThree: number | null;
  fewestMovesOne: number | null;
  fewestMovesThree: number | null;
  longestStreakDays: number;
  currentStreakDays: number;
  lastWinDateLocal: string | null; // YYYY-MM-DD in user's local zone
  firstWinDateOfTodayLocal: string | null; // for first-daily multiplier
  totalUndos: number;
  totalHints: number;
  jokerCards: number;
  jokerCardsEarned: number;
  jokerCardsSpent: number;
  detailed: DetailedStats;
}

export interface DetailedStats {
  one: ModeStats;
  three: ModeStats;
}

export interface ModeStats {
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  noUndoWins: number;
  bestScore: number | null;
  totalWinScore: number;
  bestTimeSec: number | null;
  totalWinTimeSec: number;
  longestWinTimeSec: number | null;
  bestMoves: number | null;
  totalWinMoves: number;
  mostWinMoves: number | null;
  currentWinStreak: number;
  longestWinStreak: number;
}

export const DEFAULT_MODE_STATS: ModeStats = {
  gamesPlayed: 0,
  gamesWon: 0,
  gamesLost: 0,
  noUndoWins: 0,
  bestScore: null,
  totalWinScore: 0,
  bestTimeSec: null,
  totalWinTimeSec: 0,
  longestWinTimeSec: null,
  bestMoves: null,
  totalWinMoves: 0,
  mostWinMoves: null,
  currentWinStreak: 0,
  longestWinStreak: 0,
};

export const DEFAULT_DETAILED_STATS: DetailedStats = {
  one: { ...DEFAULT_MODE_STATS },
  three: { ...DEFAULT_MODE_STATS },
};

export const DEFAULT_STATS: Stats = {
  cumulativeXp: 0,
  gamesPlayed: 0,
  gamesWon: 0,
  bestTimeSecOne: null,
  bestTimeSecThree: null,
  fewestMovesOne: null,
  fewestMovesThree: null,
  longestStreakDays: 0,
  currentStreakDays: 0,
  lastWinDateLocal: null,
  firstWinDateOfTodayLocal: null,
  totalUndos: 0,
  totalHints: 0,
  jokerCards: 0,
  jokerCardsEarned: 0,
  jokerCardsSpent: 0,
  detailed: {
    one: { ...DEFAULT_MODE_STATS },
    three: { ...DEFAULT_MODE_STATS },
  },
};

export interface Settings {
  drawMode: DrawMode;
  scoringSystem: ScoringSystem;
  soundEnabled: boolean;
  musicEnabled: boolean;
  vibrateEnabled: boolean;
  foundationOnLeft: boolean;
  language: "ja" | "en";
  theme: "default";
}

export const DEFAULT_SETTINGS: Settings = {
  drawMode: "one",
  scoringSystem: DEFAULT_SCORING_SYSTEM,
  soundEnabled: true,
  musicEnabled: false,
  vibrateEnabled: true,
  foundationOnLeft: true,
  language: "ja",
  theme: "default",
};

export function loadStats(): Stats {
  try {
    const raw = localStorage.getItem(K_STATS);
    if (!raw) return { ...DEFAULT_STATS };
    return normalizeStats(JSON.parse(raw) as Partial<Stats>);
  } catch {
    return { ...DEFAULT_STATS };
  }
}

export function saveStats(s: Stats): void {
  localStorage.setItem(K_STATS, JSON.stringify(s));
}

export function resetDetailedStats(stats: Stats): Stats {
  return {
    ...stats,
    detailed: {
      one: { ...DEFAULT_MODE_STATS },
      three: { ...DEFAULT_MODE_STATS },
    },
  };
}

function normalizeStats(raw: Partial<Stats>): Stats {
  return {
    ...DEFAULT_STATS,
    ...raw,
    detailed: {
      one: {
        ...DEFAULT_MODE_STATS,
        ...(raw.detailed?.one ?? {}),
      },
      three: {
        ...DEFAULT_MODE_STATS,
        ...(raw.detailed?.three ?? {}),
      },
    },
  };
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(K_SETTINGS);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: Settings): void {
  localStorage.setItem(K_SETTINGS, JSON.stringify(s));
}

export function loadProgress(): GameState | null {
  try {
    const raw = localStorage.getItem(K_PROGRESS);
    if (!raw) return null;
    return normalizeProgress(JSON.parse(raw) as GameState);
  } catch {
    return null;
  }
}

export function saveProgress(g: GameState | null): void {
  if (!g || g.finished) {
    localStorage.removeItem(K_PROGRESS);
    return;
  }
  // Persist the full undo stack so a reload mid-game can still rewind.
  // Cap the depth defensively to avoid storage bloat on extreme play sessions.
  const MAX_UNDO_DEPTH = 500;
  const stack = g.undoStack.length > MAX_UNDO_DEPTH
    ? g.undoStack.slice(g.undoStack.length - MAX_UNDO_DEPTH)
    : g.undoStack;
  const persisted: GameState = { ...g, undoStack: stack };
  try {
    localStorage.setItem(K_PROGRESS, JSON.stringify(persisted));
  } catch {
    // Storage full — fall back to slim state without undo history.
    const slim: GameState = { ...g, undoStack: [] };
    localStorage.setItem(K_PROGRESS, JSON.stringify(slim));
  }
}

function normalizeProgress(g: GameState): GameState {
  const containsJoker = [
    ...g.tableau,
    ...g.foundation,
    g.stock,
    g.waste,
  ].some((pile) => pile.some((card) => card.isJoker === true));
  return {
    ...g,
    stockRecycles: g.stockRecycles ?? 0,
    scoringSystem: g.scoringSystem ?? DEFAULT_SCORING_SYSTEM,
    score: g.score ?? 0,
    joker: {
      usedThisGame: g.joker?.usedThisGame ?? containsJoker,
      pendingSpend: g.joker?.pendingSpend ?? containsJoker,
    },
    undoStack: (g.undoStack ?? []).map((snap) => ({
      ...snap,
      stockRecycles: snap.stockRecycles ?? 0,
      score: snap.score ?? 0,
      joker: {
        usedThisGame: snap.joker?.usedThisGame ?? false,
        pendingSpend: snap.joker?.pendingSpend ?? false,
      },
    })),
  };
}

export function todayLocalDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function yesterdayLocalDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Update stats after a finished game (won or lost). Returns the updated stats and
// whether this win counts as the user's first daily win.
export function recordGameResult(
  stats: Stats,
  g: GameState,
  elapsedSec: number,
): { stats: Stats; isFirstDailyWin: boolean } {
  const next: Stats = cloneStats(stats);
  next.gamesPlayed += 1;
  next.totalUndos += g.undosUsed;
  next.totalHints += g.hintsUsed;

  let isFirstDailyWin = false;

  if (g.won) {
    recordDetailedWin(next, g, elapsedSec);
    next.gamesWon += 1;
    const today = todayLocalDate();
    const yest = yesterdayLocalDate();

    if (next.firstWinDateOfTodayLocal !== today) {
      isFirstDailyWin = true;
      next.firstWinDateOfTodayLocal = today;
    }

    // Streak update
    if (next.lastWinDateLocal === today) {
      // already counted today, streak unchanged
    } else if (next.lastWinDateLocal === yest) {
      next.currentStreakDays = (next.currentStreakDays || 0) + 1;
    } else {
      next.currentStreakDays = 1;
    }
    next.longestStreakDays = Math.max(
      next.longestStreakDays,
      next.currentStreakDays,
    );
    next.lastWinDateLocal = today;

    // Best stats per mode
    if (g.drawMode === "one") {
      next.bestTimeSecOne =
        next.bestTimeSecOne == null
          ? elapsedSec
          : Math.min(next.bestTimeSecOne, elapsedSec);
      next.fewestMovesOne =
        next.fewestMovesOne == null
          ? g.moves
          : Math.min(next.fewestMovesOne, g.moves);
    } else {
      next.bestTimeSecThree =
        next.bestTimeSecThree == null
          ? elapsedSec
          : Math.min(next.bestTimeSecThree, elapsedSec);
      next.fewestMovesThree =
        next.fewestMovesThree == null
          ? g.moves
          : Math.min(next.fewestMovesThree, g.moves);
    }
  }

  return { stats: next, isFirstDailyWin };
}

export function recordGameLoss(stats: Stats, g: GameState): Stats {
  const next = cloneStats(stats);
  const mode = next.detailed[g.drawMode];
  mode.gamesPlayed += 1;
  mode.gamesLost += 1;
  mode.currentWinStreak = 0;
  return next;
}

function recordDetailedWin(stats: Stats, g: GameState, elapsedSec: number): void {
  const mode = stats.detailed[g.drawMode];
  mode.gamesPlayed += 1;
  mode.gamesWon += 1;
  if (g.undosUsed === 0) mode.noUndoWins += 1;

  mode.bestScore = mode.bestScore == null ? g.score : Math.max(mode.bestScore, g.score);
  mode.totalWinScore += g.score;

  mode.bestTimeSec =
    mode.bestTimeSec == null ? elapsedSec : Math.min(mode.bestTimeSec, elapsedSec);
  mode.totalWinTimeSec += elapsedSec;
  mode.longestWinTimeSec =
    mode.longestWinTimeSec == null
      ? elapsedSec
      : Math.max(mode.longestWinTimeSec, elapsedSec);

  mode.bestMoves = mode.bestMoves == null ? g.moves : Math.min(mode.bestMoves, g.moves);
  mode.totalWinMoves += g.moves;
  mode.mostWinMoves =
    mode.mostWinMoves == null ? g.moves : Math.max(mode.mostWinMoves, g.moves);

  mode.currentWinStreak += 1;
  mode.longestWinStreak = Math.max(mode.longestWinStreak, mode.currentWinStreak);
}

function cloneStats(stats: Stats): Stats {
  return {
    ...stats,
    detailed: {
      one: { ...stats.detailed.one },
      three: { ...stats.detailed.three },
    },
  };
}
